/* kotmo-size-breakdown-nouveau-systeme.js — Kotmo
   (grille de tailles → quantité + validations + line item properties + i18n)

   ⚠️ COPIE DEDIEE — n'affecte QUE la fiche produit sur laquelle ce fichier
   est explicitement charge. Base sur kotmo-size-breakdown.js (fichier
   original, inchange, toujours utilise par les autres fiches en prod) +
   compat avec la grille 50/50 (kotmo-nouveau-systeme.css).

   ⚠️ IMPORTANT : ne pas charger ce fichier ET kotmo-size-breakdown.js
   (l'original) sur la meme page en meme temps — les deux s'activeraient
   ensemble si le champ meta enable_size_breakdown est actif, ce qui
   dupliquerait l'interface (deux cases a cocher, deux jeux d'inputs).
*/

(function () {
  // ─── I18N ─────────────────────────────────────────────────────────────────────────
  const I18N = {
    en: {
      toggle_label: "I already know the sizes I need",
      total_label: "Total",
      min_required_label: (min) => `Minimum required: ${min}`,
      hint_min_missing: (min, missing) =>
        `Minimum ${min} — ${missing} item${missing > 1 ? "s" : ""} missing.`,
      hint_step: (step) => (step > 1 ? `Multiple of ${step}.` : ""),
      prop_prefix: "Sizes – ",
      prop_summary: "Sizes – summary",
      prop_json: "_Sizes – JSON",
      prop_total: "_Sizes – total",
    },
    fr: {
      toggle_label: "Je connais déjà les tailles que je veux",
      total_label: "Total",
      min_required_label: (min) => `Minimum requis : ${min}`,
      hint_min_missing: (min, missing) =>
        `Minimum ${min} — il manque ${missing} unité${missing > 1 ? "s" : ""}.`,
      hint_step: (step) => (step > 1 ? `Multiple de ${step}.` : ""),
      prop_prefix: "Tailles – ",
      prop_summary: "Tailles – récap",
      prop_json: "_Tailles – JSON",
      prop_total: "_Tailles – total",
    },
  };

  const LOCALE = (() => {
    const htmlLang = (document.documentElement.getAttribute("lang") || "").toLowerCase();
    if (htmlLang.startsWith("en")) return "en";
    if ((location.pathname || "").match(/(^|\/)en(\/|$)/)) return "en";
    return "fr";
  })();
  const T = I18N[LOCALE] || I18N.en;

  // ─── Sélecteurs / état ────────────────────────────────────────────────────────────
  const SELECTORS = {
    qtyInput: 'input[name="quantity"]',
    insertBefore: '.product-form__buttons',
    // KOTMO — fallback dedie : sur le template "nouveau-systeme", le bloc
    // standard .product-form__buttons n'existe pas (le widget de
    // personnalisation fournit deja son propre bouton d'achat). Sans ce
    // fallback, init() s'arrete silencieusement des que beforeEl est null,
    // et l'interface (case a cocher + grille) ne s'affiche jamais.
    insertBeforeFallback: '#kotmo-perso-root',
    variantPickerBlock: '.product__variant-picker.product__block',
    productForms: 'form[action*="/cart/add"]',
    productRoot: 'product-info[data-main-product]',
  };

  const state = {
    active: false, // toggle coché
    lastQty: 0,    // dernière quantité imposée aux inputs
    lastMap: {},   // dernières quantités par taille
  };

  // ─── Règles min/step/max ──────────────────────────────────────────────────────────
  function getKotmoRules() {
    try {
      const tag = document.getElementById('kotmo-qty-rules');
      let min = 1, step = 1, max = Infinity;
      if (tag) {
        const obj = JSON.parse(tag.textContent || '{}');
        min = Number(obj.min) || 1;
        step = Number(obj.step) || 1;
        max = obj.max == null ? Infinity : Number(obj.max);
      }

      // KOTMO — le minimum "officiel" de ce produit vient du champ meta
      // custom.quantit_minimum (metaobjet "Quantite minimum"), deja lu par
      // kotmo-personnalisation.js via l'attribut data-qty-min sur
      // #kotmo-perso-root. On priorise cette source pour que le repartiteur
      // de tailles et le widget de personnalisation appliquent toujours le
      // meme minimum, plutot que de dependre separement de kotmo.min_qty
      // (souvent non configure sur les produits utilisant ce nouveau champ).
      const root = document.getElementById('kotmo-perso-root');
      const rootMin = root ? Number(root.dataset.qtyMin) : NaN;
      if (!isNaN(rootMin) && rootMin > 0) min = rootMin;

      return { min, step, max };
    } catch { return { min: 1, step: 1, max: Infinity }; }
  }

  function getHandle() {
    const el = document.querySelector('product-info[data-product-handle]');
    return el?.getAttribute('data-product-handle') ||
           (location.pathname.split('/products/')[1] || '').split('/')[0];
  }

  // ─── Récup tailles via /products/<handle>.js ──────────────────────────────────────
  async function getSizes() {
    const handle = getHandle();
    if (!handle) return [];
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return [];
      const product = await res.json();
      const options = product.options || [];
      const sizeOpt = options.find(o =>
        ['size','sizes','taille','tailles'].includes((o.name||'').toLowerCase())
      );
      let sizes = sizeOpt?.values || [];

      if (!sizes.length && product.variants) {
        const set = new Set();
        product.variants.forEach(v => {
          ['option1','option2','option3'].forEach((k,i)=>{
            const nm = (options[i]?.name || '').toLowerCase();
            if (['size','sizes','taille','tailles'].includes(nm) && v[k]) set.add(v[k]);
          });
        });
        sizes = Array.from(set);
      }

      const order = ['XXS','XS','S','M','L','XL','XXL','2XL','3XL','4XL','5XL'];
      sizes.sort((a,b)=>{
        const ia = order.indexOf(String(a).toUpperCase());
        const ib = order.indexOf(String(b).toUpperCase());
        if (ia === -1 && ib === -1) return String(a).localeCompare(String(b));
        if (ia === -1) return 1;
        if (ib === -1) return -1;
        return ia - ib;
      });

      return sizes;
    } catch { return []; }
  }

  // ─── Activation via méta-champ sur product-info ───────────────────────────────────
  function isEnabled() {
    const el = document.querySelector('[data-kotmo-enable-size-breakdown]');
    const val = String(el?.getAttribute('data-kotmo-enable-size-breakdown') || '').toLowerCase();
    return val === 'true' || val === '1';
  }

  // ─── Masquer uniquement l’option “Taille” (FR/EN) ─────────────────────────────────
  function hideSizeVariantOptionOnly() {
    const containers = document.querySelectorAll(SELECTORS.variantPickerBlock);
    const SIZE_KEYS = ['size', 'sizes', 'taille', 'tailles'];
    const matchesKey = (str) => str && SIZE_KEYS.some(k => String(str).toLowerCase().includes(k));

    containers.forEach(container => {
      const groups = container.querySelectorAll(
        'fieldset, .product-form__input, .product-option, .product__form-item, [data-option-name], [data-option-index]'
      );

      groups.forEach(group => {
        if (group.hasAttribute('data-kotmo-size-hidden')) return;
        const labelEl =
          group.querySelector('legend, .form__label, .product-option__label, label') || group;
        const explicitName = group.getAttribute('data-option-name') || '';
        const dataLabel = group.getAttribute('aria-label') || '';
        const labelName  = labelEl.textContent || '';
        if ([explicitName, dataLabel, labelName].some(matchesKey)) {
          const target = group.closest('.product-form__input, .product-option, fieldset, .form__input') || group;
          target.style.display = 'none';
          group.setAttribute('data-kotmo-size-hidden', 'true');

          // KOTMO — compat grille 50/50, dediee a cette fiche uniquement :
          // marque le conteneur <product-variant-selects> pour que le
          // groupe restant (ex. Couleur) reprenne toute la largeur plutot
          // que de laisser un vide a cote (voir kotmo-nouveau-systeme.css).
          const gridParent = target.closest('product-variant-selects');
          if (gridParent) gridParent.classList.add('kotmo-size-hidden');
        }
      });

      container.querySelectorAll('select[name^="options["], [role="radiogroup"]').forEach(el => {
        const already = el.closest('[data-kotmo-size-hidden="true"]');
        if (already) return;
        const id = el.getAttribute('id');
        const labelTxt = (id ? container.querySelector(`label[for="${id}"]`)?.textContent : '') || '';
        const aria = el.getAttribute('aria-label') || '';
        const dataName = el.getAttribute('data-option-name') || '';
        if ([labelTxt, aria, dataName].some(matchesKey)) {
          const wrap = el.closest('.product-form__input, .product-option, fieldset, .form__input') || el;
          wrap.style.display = 'none';
          wrap.setAttribute('data-kotmo-size-hidden', 'true');

          // KOTMO — compat grille 50/50, meme principe que ci-dessus pour
          // ce second chemin (select/radiogroup).
          const gridParent = wrap.closest('product-variant-selects');
          if (gridParent) gridParent.classList.add('kotmo-size-hidden');
        }
      });
    });
  }

  // Observe re-hydration / sections dynamiques et re-cacher l’option “Taille”
  function observeVariantPicker() {
    const target = document.querySelector(SELECTORS.variantPickerBlock) || document.body;
    if (!target) return;
    let raf;
    const debounced = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        hideSizeVariantOptionOnly();
        if (state.active && state.lastQty > 0) applyToQuantityAll(state.lastQty, true);
        wireSubmitGuards();
      });
    };
    const obs = new MutationObserver(debounced);
    obs.observe(target, { childList: true, subtree: true, attributes: true });
    document.addEventListener('shopify:section:load', debounced);
    document.addEventListener('shopify:section:select', debounced);
    document.addEventListener('shopify:section:deselect', debounced);
  }

  // ─── Helpers quantité / prix / boutons / propriétés ───────────────────────────────
  function getAllQtyInputs() {
    return Array.from(document.querySelectorAll(`${SELECTORS.productForms} ${SELECTORS.qtyInput}`));
  }

  // Compat prix (DPO/APO...) : avertir les moteurs après changement de quantité
  function notifyPriceEngines() {
    try {
      document.dispatchEvent(new CustomEvent('dpo:quantity-changed', { bubbles: true }));
      if (window.itorisDPO?.recalculatePrice) window.itorisDPO.recalculatePrice();
      if (window.itorisDPO?.recalculate)      window.itorisDPO.recalculate();
      if (window.DPO?.recalculate)            window.DPO.recalculate();
      if (window.MwApo?.recalculate)          window.MwApo.recalculate();
    } catch (e) { /* silent */ }
  }

  // KOTMO — synchronise le moteur de personnalisation natif (kotmo-personnalisation.js)
  // avec la quantite reelle issue de la grille de tailles. Sans cet appel, le
  // widget de personnalisation garde sa propre quantite interne (par ex. celle
  // du dernier palier de volume selectionne) et affiche un prix total qui ne
  // correspond plus a ce qui a ete saisi ici. belowMinimum/minRequired permettent
  // au widget de griser son propre bouton "Ajouter au panier" quand le minimum
  // n'est pas atteint (setAddButtonsDisabled ci-dessous ne cible que les boutons
  // natifs du thème, jamais le bouton du widget qui est de type="button").
  function notifyPersoWidget(qty, belowMinimum, minRequired, sizeMap) {
    try {
      document.dispatchEvent(new CustomEvent('kotmo:price-update', {
        detail: { quantity: qty, belowMinimum: !!belowMinimum, minimumRequired: minRequired, sizeBreakdown: sizeMap || {} },
      }));
    } catch (e) { /* silent */ }
  }

  function applyToQuantityAll(v, force = false) {
    getAllQtyInputs().forEach(qtyEl => {
      if (!qtyEl) return;
      if (!force && qtyEl.readOnly) return;
      qtyEl.value = String(v);
      qtyEl.dispatchEvent(new Event('input',  { bubbles: true }));
      qtyEl.dispatchEvent(new Event('change', { bubbles: true }));
    });
    notifyPriceEngines();
  }

  function setAddButtonsDisabled(flag){
    const btns = document.querySelectorAll(
      `${SELECTORS.productForms} [type="submit"], .product__sticky-cart [type="submit"], .shopify-payment-button__button`
    );
    btns.forEach(btn => {
      if (!btn) return;
      btn.disabled = !!flag;
      btn.classList.toggle('is-disabled', !!flag);
      btn.classList.toggle('disabled', !!flag);
      btn.setAttribute('aria-disabled', flag ? 'true' : 'false');
      btn.style.pointerEvents = flag ? 'none' : '';
      if (btn.classList.contains('shopify-payment-button__button')) {
        btn.style.opacity = flag ? '0.5' : '';
      }
    });
  }

  function ensureHidden(form, name, value) {
    Array.from(form.querySelectorAll(`input[name="${CSS.escape(name)}"]`)).forEach(n => n.remove());
    if (value === '' || value == null) return;
    const i = document.createElement('input');
    i.type = 'hidden';
    i.name = name;
    i.value = value;
    form.appendChild(i);
  }

  // ❗ On ne purge/écrit QUE nos propres propriétés (prefix Tailles/Sizes) → safe DPO
  function syncPropertiesToAllForms(mapBySize, totalForRecord) {
    const forms = document.querySelectorAll(SELECTORS.productForms);
    const prettyList = Object.entries(mapBySize)
      .filter(([,q]) => q > 0)
      .map(([s,q]) => `${s}: ${q}`)
      .join(', ');
    const jsonValue = JSON.stringify(mapBySize);

    forms.forEach(form => {
      Array.from(form.querySelectorAll(
        'input[name^="properties[Tailles"], input[name^="properties[_Tailles"], input[name^="properties[Sizes"], input[name^="properties[_Sizes"]'
      )).forEach(n => n.remove());

      Object.entries(mapBySize).forEach(([size, qty]) => {
        if (qty > 0) ensureHidden(form, `properties[_${T.prop_prefix}${size}]`, String(qty));
      });

      ensureHidden(form, `properties[${T.prop_summary}]`, prettyList || '');
      ensureHidden(form, `properties[${T.prop_json}]`, jsonValue);
      ensureHidden(form, `properties[${T.prop_total}]`, String(totalForRecord));
    });
  }

  // ─── Garde submit si min non atteint ───────────────────────────────────────────────
  function wireSubmitGuards() {
    document.querySelectorAll(SELECTORS.productForms).forEach(form => {
      if (form.hasAttribute('data-kotmo-submit-guard')) return;
      form.setAttribute('data-kotmo-submit-guard', '1');
      form.addEventListener('submit', (e) => {
        if (!state.active) return;
        const total = state.lastQty || 0;
        const rules = getKotmoRules();
        if (total < rules.min) { e.preventDefault(); e.stopPropagation(); }
        else {
          applyToQuantityAll(total, true);
          syncPropertiesToAllForms(state.lastMap, total);
        }
      }, { capture: true });
    });
  }

  // ─── UI ────────────────────────────────────────────────────────────────────────────
  function buildUI(sizes, rules) {
    const wrap = document.createElement('div');
    wrap.className = 'kotmo-size-breakdown';
    wrap.innerHTML = `
      <div class="ksb-card">
        <label class="ksb-check">
          <input type="checkbox" class="ksb-toggle" aria-controls="ksb-panel" />
          <span>${T.toggle_label}</span>
        </label>
        <div id="ksb-panel" class="ksb-panel" hidden>
          <div class="ksb-grid">
            ${sizes.map(s => `
              <div class="ksb-cell">
                <div class="ksb-size">${s}</div>
                <input class="ksb-input" inputmode="numeric" pattern="\\d*" min="0" data-size="${s}" placeholder="0" />
              </div>`).join('')}
          </div>
          <div class="ksb-footer">
            <div class="ksb-total">
              ${T.total_label} : <strong class="ksb-total-value">0</strong>
              <span class="ksb-min-required">${T.min_required_label(rules.min)}</span>
            </div>
            <div class="ksb-hint" aria-live="polite"></div>
          </div>
        </div>
      </div>
    `;

    // (styles minimaux, ton CSS peut tout écraser)
    const style = document.createElement('style');
    style.textContent = `
      .ksb-card{background:#F5F1EA;border-radius:12px;padding:18px;margin-bottom:20px}
      .ksb-check{display:flex;align-items:center;gap:10px;font-size:17px;font-weight:500}
      .ksb-panel{margin-top:16px}
      .ksb-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(100px,1fr));gap:12px}
      .ksb-cell{background:#fff;border:1px solid #ddd;border-radius:10px;padding:10px;text-align:center}
      .ksb-size{font-weight:600;margin-bottom:8px}
      .ksb-input{width:100%;font-size:18px;text-align:center;padding:6px;border:1px solid #ccc;border-radius:6px}
      .ksb-footer{display:flex;justify-content:space-between;margin-top:12px;font-size:14px}
      .ksb-min-required{margin-left:10px;opacity:.65;font-size:13px}
      .ksb-hint{opacity:.75}
    `;
    wrap.appendChild(style);

    const toggle  = wrap.querySelector('.ksb-toggle');
    const panel   = wrap.querySelector('#ksb-panel');
    const totalEl = wrap.querySelector('.ksb-total-value');
    const hint    = wrap.querySelector('.ksb-hint');

    const getInputs = () => Array.from(wrap.querySelectorAll('.ksb-input'));
    const rawTotal  = () => getInputs().reduce((s,el)=>s+(parseInt(el.value,10)||0),0);
    const mapBySize = () => {
      const m = {}; getInputs().forEach(el => { m[el.dataset.size] = parseInt(el.value,10) || 0; });
      return m;
    };

    function clampWithoutMin(t, rules){
      let v = Math.max(0, Number(t) || 0);
      if (rules.step > 1) v = Math.ceil(v / rules.step) * rules.step;
      if (v > rules.max) v = rules.max;
      return v;
    }

    function updateTotal(rules) {
      const raw = rawTotal();
      totalEl.textContent = String(raw);
      state.lastMap = mapBySize();

      if (raw < rules.min) {
        const missing = rules.min - raw;
        hint.textContent = T.hint_min_missing(rules.min, missing);
        setAddButtonsDisabled(true);
        state.lastQty = raw;
        applyToQuantityAll(raw);
        syncPropertiesToAllForms(state.lastMap, raw);
        notifyPersoWidget(raw, true, rules.min, state.lastMap);
        return;
      }

      setAddButtonsDisabled(false);
      hint.textContent = T.hint_step(rules.step);
      const clamped = clampWithoutMin(raw, rules);
      state.lastQty = clamped;
      applyToQuantityAll(clamped);
      syncPropertiesToAllForms(state.lastMap, clamped);
      notifyPersoWidget(clamped, false, rules.min, state.lastMap);
    }

    wrap.addEventListener('input', e => {
      if (e.target.classList.contains('ksb-input')) {
        e.target.value = e.target.value.replace(/[^\d]/g,'');
        updateTotal(getKotmoRules());
      }
    });

    // Gestion propre au (dé)cochage
    toggle.addEventListener('change', () => {
      state.active = toggle.checked;
      panel.hidden = !state.active;

      // quantity en lecture seule seulement si actif
      getAllQtyInputs().forEach(q => { if (q) q.readOnly = state.active; });

      const rules = getKotmoRules();

      if (state.active) {
        updateTotal(rules);
      } else {
        hint.textContent = '';
        state.lastMap = {};
        const current = clampWithoutMin(Number(getAllQtyInputs()[0]?.value || 0), rules);
        state.lastQty = current;
        applyToQuantityAll(current, true);            // réveille les validateurs + DPO
        setAddButtonsDisabled(current < rules.min);   // état correct du bouton
        syncPropertiesToAllForms({}, current);        // nettoie nos props
        notifyPersoWidget(current, current < rules.min, rules.min, state.lastMap);
      }
    });

    wireSubmitGuards();

    // Certains thèmes émettent variant:change → on réapplique la qty mémorisée
    document.addEventListener('variant:change', () => {
      if (state.active && state.lastQty > 0) applyToQuantityAll(state.lastQty, true);
    }, true);

    return wrap;
  }

  // ─── INIT ──────────────────────────────────────────────────────────────────────────
  async function init() {
    if (!isEnabled()) return;
    const rules = getKotmoRules();
    const sizes = await getSizes();
    if (!sizes.length) return;

    hideSizeVariantOptionOnly();
    observeVariantPicker();

    const beforeEl = document.querySelector(SELECTORS.insertBefore)
      || document.querySelector(SELECTORS.insertBeforeFallback);
    if (!beforeEl) return;

    const ui = buildUI(sizes, rules);
    beforeEl.parentNode.insertBefore(ui, beforeEl);

    // Si le thème remplace des bouts du formulaire, on ré-impose la qty & les guards
    const root = document.querySelector(SELECTORS.productRoot) || document.querySelector('product-info') || document.body;
    const obs = new MutationObserver(() => {
      if (state.active && state.lastQty > 0) {
        applyToQuantityAll(state.lastQty, true);
        wireSubmitGuards();
      }
    });
    obs.observe(root, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();